import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import {
    useQueryFiltersFromFiltersAtom,
    useVisitFiltersFromFiltersAtom,
} from '@/types/visitRequest';
import {
    atomWithSessionStorage,
    optionalMomentAtomWithSessionStorage,
} from '@/utils/browserStorage';
import { VisitFilter, VisitRequestFilter } from 'gql-types';
import { SetStateAction, useAtom } from 'jotai';
import moment, { Moment } from 'moment';
import { Dispatch, FC, ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { z } from 'zod';

import { QueueTab } from './queues/types';

interface IVisitRequestListPageContext {
    // Filters
    dialogFilters: VisitRequestFilter;
    visitDialogFilters: VisitFilter;
    numDialogFilters: number;
    selectedDateRangeFilter: VisitRequestFilter;
    selectedVisitDateRangeFilter: VisitFilter;
    passedFilters?: VisitRequestFilter;
    selectedIds: [selectedIds: string[], setSelectedIds: Dispatch<SetStateAction<string[]>>];
    // Queue tab
    queueTab: QueueTab;
    setQueueTab: (queueTab: QueueTab) => void;

    // Date range
    startDate: Moment | null;
    setStartDate: (startDate: Moment | null) => void;
    endDate: Moment | null;
    setEndDate: (endDate: Moment | null) => void;
}

const VisitRequestListPageContext = createContext<IVisitRequestListPageContext | null>(null);

// Persisted date range selection.
const today = moment().startOf('day');
const startDateAtom = optionalMomentAtomWithSessionStorage('visitRequestsListStartDate', today);
const endDateAtom = optionalMomentAtomWithSessionStorage(
    'visitRequestsListEndDate',
    today.clone().add(6, 'days'),
);

// Persisted queue tab selection.
export const selectedVisitRequestsQueueTab = atomWithSessionStorage(
    'visitRequestsQueueTab',
    QueueTab.ONE_OFF_VISIT_REQUESTS,
    z.nativeEnum(QueueTab),
);

type VisitRequestListPageContextProps = {
    children: ReactNode;
    passedFilters?: VisitRequestFilter;
};

export const VisitRequestListPageContextProvider: FC<VisitRequestListPageContextProps> = (
    props,
) => {
    const { children, passedFilters } = props;
    const [queueTab, setQueueTab] = useAtom(selectedVisitRequestsQueueTab);
    const [startDate, setStartDate] = useAtom(startDateAtom);
    const [endDate, setEndDate] = useAtom(endDateAtom);
    const selectedIds = useState<string[]>([]);

    const { queryFilters: dialogFilters, numFilters: numDialogFilters } =
        useQueryFiltersFromFiltersAtom();
    const visitDialogFilters = useVisitFiltersFromFiltersAtom();

    const selectedDateRangeFilter = useMemo(() => {
        if (!startDate && !endDate) {
            return {};
        }
        return {
            plannedInDateWindow: {
                startDate: GraphInputHelpers.date(startDate),
                endDate: GraphInputHelpers.date(endDate),
            },
        };
    }, [startDate, endDate]);

    const selectedVisitDateRangeFilter = useMemo((): VisitFilter => {
        if (!startDate || !endDate) {
            return {};
        }
        return {
            scheduledOrPlannedInDateWindow: {
                startDate: GraphInputHelpers.date(startDate),
                endDate: GraphInputHelpers.date(endDate),
            },
        };
    }, [startDate, endDate]);

    const value = useMemo(
        () => ({
            selectedIds,
            dialogFilters,
            visitDialogFilters,
            numDialogFilters,
            selectedDateRangeFilter,
            selectedVisitDateRangeFilter,
            queueTab,
            setQueueTab,
            startDate,
            setStartDate,
            endDate,
            setEndDate,
            passedFilters,
        }),
        [
            dialogFilters,
            visitDialogFilters,
            endDate,
            numDialogFilters,
            passedFilters,
            queueTab,
            selectedDateRangeFilter,
            selectedVisitDateRangeFilter,
            selectedIds,
            setEndDate,
            setQueueTab,
            setStartDate,
            startDate,
        ],
    );

    return (
        <VisitRequestListPageContext.Provider value={value}>
            {children}
        </VisitRequestListPageContext.Provider>
    );
};

export const useVisitRequestListPageContext = () => {
    const context = useContext(VisitRequestListPageContext);
    if (!context) {
        throw new Error(
            'useVisitRequestListPageContext must be used within a VisitRequestListPageContextProvider',
        );
    }
    return context;
};
